import React, { useEffect } from 'react'

import { useModalContext, useBanner } from '@sketch/components'

import { ProjectFragment, useGetWorkspaceQuery } from '@sketch/gql-types'

import { BannerButton } from '../../components/ProjectShares/ProjectShares.styles'
import { MoveStorageItemModal } from '../../../storageItems'

interface UseProjectBannerProps {
  project?: Pick<
    ProjectFragment,
    | 'deletedAt'
    | 'archivedAt'
    | '__typename'
    | 'identifier'
    | 'name'
    | 'parentProjectIdentifier'
  >
  workspaceId: string
}

export const useProjectBanner = ({
  project,
  workspaceId,
}: UseProjectBannerProps) => {
  const { showBanner } = useBanner()
  const { showModal } = useModalContext()

  const { data } = useGetWorkspaceQuery({
    variables: { identifier: workspaceId },
    fetchPolicy: 'cache-only',
  })

  const isGuest = data?.workspace?.userRole === 'GUEST'

  useEffect(() => {
    if (project?.deletedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            The <b>“{project.name}”</b> folder was deleted.{' '}
            {!isGuest && (
              <>
                <BannerButton
                  onClick={() => {
                    showModal(MoveStorageItemModal, {
                      items: [project],
                      workspaceIdentifier: workspaceId,
                      parentProjectIdentifier: project.parentProjectIdentifier,
                      originType: 'stashed',
                      action: 'restore',
                    })
                  }}
                >
                  Restore the folder
                </BannerButton>
                , to edit its documents.
              </>
            )}
          </>
        ),
      })

      return dismissBanner
    }

    if (project?.archivedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            This folder is archived. You can view and edit archived documents as
            usual.
          </>
        ),
      })

      return dismissBanner
    }
  }, [showModal, showBanner, project, workspaceId, isGuest])
}
